import { useController, type Control } from 'react-hook-form';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { AlertCircle } from 'lucide-react';
import { HelpCircle } from 'lucide-react';
import type { PolicySetting } from '@kirin/api-client/contracts/policies';
import type { PolicySettingsFormData } from '../types';
import { validatePolicySetting } from '../helpers/validation';
import { BooleanSettingField } from './BooleanSettingField';
import { ModeSettingCard } from './ModeSettingCard';
import { StringSettingField } from './StringSettingField';
import { ArraySettingField } from './ArraySettingField';
import { PiiTypesField } from './PiiTypesField';
import { GitleaksRulesLink } from './GitleaksRulesLink';
import { BetaBadge } from './BetaBadge';
import { hasBetaBadge } from '../helpers/patternModalRegistry';
import { ComingSoonBadge } from './ComingSoonBadge';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { PolicyMode } from '@kirin/api-client/contracts/policies';
import { ENFORCE_ONLY_SETTING_NOTE, isEnforceOnlySetting } from '../constants';
import { useParentPolicyMode } from './parentPolicyMode';
interface PolicySettingFieldProps {
  setting: PolicySetting;
  policyKey: string;
  control: Control<PolicySettingsFormData>;
  readOnly?: boolean;
  /** Support role: pattern-modal rows stay viewable/editable, only their
   *  commit actions (Edit Rule submit, Delete) are disabled. Ignored by
   *  non-array field types. */
  isSupportReadOnly?: boolean;
  hidden?: boolean;
  isDirty?: boolean;
}

/**
 * Policy setting field component
 * Renders the appropriate input component based on setting type (boolean, mode, string, array)
 * Handles validation and error display using react-hook-form
 *
 * @param setting - The policy setting configuration
 * @param policyKey - The policy key for component type detection
 * @param control - The react-hook-form control object
 * @param readOnly - Whether the field is read-only
 * @param hidden - Whether the field should be hidden (but still registered with form)
 */
export function PolicySettingField({
  setting,
  policyKey,
  control,
  readOnly = false,
  isSupportReadOnly = false,
  hidden = false,
  isDirty = false,
}: PolicySettingFieldProps) {
  // KIR-3612: an enforce-only action stays editable but notes that it waits
  // for Enforce. No parent mode (a field rendered outside the form) shows no note.
  const { mode: parentMode } = useParentPolicyMode();
  const waitsForEnforce =
    isEnforceOnlySetting(policyKey, setting.key) &&
    parentMode != null &&
    parentMode !== PolicyMode.Enforce;
  const {
    field,
    fieldState: { error },
  } = useController({
    name: setting.key,
    control,
    defaultValue: setting.value ?? setting.defaultValue,
    rules: {
      // Required validation: show error if field is required but empty
      required: setting.validation?.required
        ? `${setting.name}: This field is required`
        : false,
      // Custom validation: validates based on setting type and validation rules
      // This provides immediate feedback to users before submission
      validate: value => validatePolicySetting(setting, value, policyKey),
    },
  });

  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.GITLEAKS_RULE_MANAGEMENT,
  ]);
  const isGitleaksRuleManagementEnabled =
    flags[flagsmithFeatureFlags.GITLEAKS_RULE_MANAGEMENT] ?? false;

  // KIR-4590: with the editor link in place the group heading already reads
  // "Secret Detection Rules"; the setting's own "Disabled Secret Detection
  // Rules" label would mislabel a control that lists all 222 rules, so the
  // label row is dropped.
  const usesGitleaksRulesEditor =
    isGitleaksRuleManagementEnabled &&
    policyKey === 'secret-detection' &&
    setting.key === 'disabledGitleaksRules';

  const renderField = () => {
    // KIR-3837: the PII "Detected PII Types" list is a bespoke table + DLP picker
    // (per-type toggle, sensitivity badge, higher-latency tag), not the generic
    // string-array input. It manages both `infoTypes` (enabled) and
    // `selectedInfoTypes` (all rows) via the form control.
    if (policyKey === 'pii-detection' && setting.key === 'infoTypes') {
      return <PiiTypesField control={control} readOnly={readOnly} />;
    }
    // KIR-4590: 222 rule IDs are unusable as a chip list, so the row becomes a
    // link to the editor. Flag off falls through to today's chip list.
    if (usesGitleaksRulesEditor) {
      return (
        <GitleaksRulesLink
          disabledCount={Array.isArray(field.value) ? field.value.length : 0}
          readOnly={readOnly}
        />
      );
    }
    switch (setting.type) {
      case 'boolean':
        return <BooleanSettingField field={field} disabled={readOnly} />;
      case 'string':
        return (
          <StringSettingField
            setting={setting}
            field={field}
            disabled={readOnly}
          />
        );
      case 'array':
        return (
          <ArraySettingField
            setting={setting}
            policyKey={policyKey}
            field={field}
            readOnly={readOnly}
            isSupportReadOnly={isSupportReadOnly}
            isDirty={isDirty}
          />
        );
      default:
        return null;
    }
  };

  // If hidden, render only a hidden input to preserve the value in form data
  if (hidden) {
    return (
      <input
        type="hidden"
        {...field}
        value={
          typeof field.value === 'object'
            ? JSON.stringify(field.value)
            : String(field.value ?? '')
        }
      />
    );
  }

  // KIR-4426: a nested mode sub-policy renders as a card with its description
  // visible, not as a label + help-icon row — see ModeSettingCard.
  if (setting.type === 'mode') {
    return (
      <div className="policy-setting-group">
        <ModeSettingCard setting={setting} field={field} readOnly={readOnly} />
        {error && (
          <div className="text-xs text-red-500 mt-1">{error.message}</div>
        )}
        {setting.isCustomized && (
          <Badge variant="outline" className="text-xs mt-2">
            Customized
          </Badge>
        )}
      </div>
    );
  }

  return (
    <div className="policy-setting-group">
      {!usesGitleaksRulesEditor && (
        <div className="policy-setting-label">
          <div className="flex items-center gap-2">
            <Label className="text-sm font-medium" htmlFor={setting.key}>
              {setting.name}
            </Label>
            {hasBetaBadge(policyKey, setting.key) && <BetaBadge />}
            {policyKey === 'cursor-database' &&
              setting.key === 'enableCursorAIAnalysis' && <ComingSoonBadge />}
            {setting.validation?.required && (
              <span className="text-red-500 text-xs">*</span>
            )}
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger type="button">
                <HelpCircle className="h-4 w-4 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="policy-setting-description">
                  {setting.description}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}

      {renderField()}

      {waitsForEnforce && (
        <div className="text-xs text-muted-foreground mt-1">
          {ENFORCE_ONLY_SETTING_NOTE}
        </div>
      )}

      {error && (
        <div className="text-xs text-red-500 mt-1">{error.message}</div>
      )}

      {/* Windows disclaimer for Extensions Code Scan */}
      {policyKey === 'extension-security' &&
        setting.key === 'extensionsCodeScan' &&
        field.value === true && (
          <div className="flex items-start gap-2 mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
            <AlertCircle className="h-4 w-4 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-yellow-800 dark:text-yellow-200">
              <strong>Note:</strong> Extensions Code Scan is not supported on
              Windows at the moment. (coming soon)
            </p>
          </div>
        )}

      {/* Warning for Auto Remove Vulnerable Extensions */}
      {policyKey === 'extension-security' &&
        setting.key === 'autoRemoveVulnerableExtensions' &&
        field.value === true && (
          <div className="flex items-start gap-2 mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
            <AlertCircle className="h-4 w-4 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-yellow-800 dark:text-yellow-200">
              <strong>Warning:</strong> When enabled,{' '}
              <strong>newly detected</strong> VS Code/Cursor extensions{' '}
              <strong>with the selected severity threshold or higher</strong>{' '}
              will be <strong>permanently uninstalled</strong> from VS
              Code/Cursor. This action cannot be undone - extensions must be
              reinstalled manually from the marketplace.
            </p>
          </div>
        )}

      {setting.isCustomized && (
        <Badge variant="outline" className="text-xs mt-2">
          Customized
        </Badge>
      )}
    </div>
  );
}
